import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Phone, Warning, FirstAid, Pill, Drop, Calendar, MapPin, UsersThree, QrCode, LinkSimple, ArrowSquareOut } from "@phosphor-icons/react";

export default function PublicScan() {
    const { code } = useParams();
    const { t, lang, setLang } = useI18n();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get(`/scan/${code}`)
            .then((r) => { if (alive) { setData(r.data); if (r.data?.language) setLang(r.data.language); } })
            .catch((e) => { if (alive) setError(e.response?.data?.detail || "Not found"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line
    }, [code]);

    if (loading) return <Mini text="…" />;
    if (error === "Bracelet inactive") return <StatusScreen icon={<Warning size={64} weight="duotone" />} msg={t.scan.inactive} code={code} />;
    if (error) return <StatusScreen icon={<QrCode size={64} weight="duotone" />} msg={t.scan.unclaimed} code={code} />;
    if (data?.unclaimed) return <StatusScreen icon={<QrCode size={64} weight="duotone" />} msg={t.scan.unclaimed} code={code} />;

    const p = data.profile;
    return (
        <div className="min-h-screen bg-white text-brand-text" data-testid="scan-page">
            <div className="max-w-md mx-auto">
                {/* Critical header */}
                <header className="bg-brand-critical text-white px-5 pt-6 pb-7">
                    <div className="flex items-center justify-between">
                        <div className="font-mono tracking-widest text-[10px] uppercase">{t.scan.emergency}</div>
                        <div className="flex items-center bg-white/15 rounded-full p-0.5 text-[10px] font-mono">
                            <button onClick={() => setLang("ro")} className={`px-2 py-0.5 rounded-full ${lang === "ro" ? "bg-white text-brand-critical" : ""}`}>RO</button>
                            <button onClick={() => setLang("en")} className={`px-2 py-0.5 rounded-full ${lang === "en" ? "bg-white text-brand-critical" : ""}`}>EN</button>
                        </div>
                    </div>
                    <h1 className="font-display text-4xl mt-3 leading-tight" data-testid="scan-name">{p.full_name || "—"}</h1>
                    <div className="mt-2 flex gap-2 text-xs font-mono tracking-widest">
                        {p.blood_type && <span className="px-2 py-1 bg-white/20 rounded-full" data-testid="scan-blood">{t.scan.blood}: {p.blood_type}</span>}
                        {p.date_of_birth && <span className="px-2 py-1 bg-white/20 rounded-full">{p.date_of_birth}</span>}
                    </div>
                </header>

                <div className="px-5 -mt-4">
                    {/* Event / external link */}
                    {p.website_url && (
                        <a
                            href={normalizeUrl(p.website_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 block bg-brand-primary text-white rounded-2xl p-5 shadow-lg"
                            data-testid="scan-event-link"
                        >
                            <div className="flex items-center gap-3">
                                <LinkSimple size={32} weight="bold" />
                                <div className="flex-1">
                                    <div className="font-display text-2xl leading-tight">{p.event_title || t.scan.openLink}</div>
                                    <div className="font-mono text-xs opacity-90 truncate mt-0.5">{p.website_url}</div>
                                </div>
                                <ArrowSquareOut size={22} weight="bold" />
                            </div>
                        </a>
                    )}

                    {/* Call buttons */}
                    {(p.emergency_contact_1_phone || p.emergency_contact_2_phone) && (
                        <div className="space-y-3" data-testid="scan-contacts">
                            <div className="font-mono tracking-widest text-[10px] uppercase text-brand-textMuted pt-4">{t.scan.contacts}</div>
                            {p.emergency_contact_1_phone && (
                                <a href={`tel:${p.emergency_contact_1_phone}`} className="block bg-brand-critical text-white rounded-2xl p-5 shadow-lg" data-testid="scan-call-1">
                                    <div className="flex items-center gap-3">
                                        <Phone size={36} weight="fill" />
                                        <div>
                                            <div className="font-display text-2xl">{p.emergency_contact_1_name || t.scan.callNow}</div>
                                            <div className="font-mono text-sm opacity-90">{p.emergency_contact_1_phone}</div>
                                        </div>
                                    </div>
                                </a>
                            )}
                            {p.emergency_contact_2_phone && (
                                <a href={`tel:${p.emergency_contact_2_phone}`} className="block bg-brand-secondary text-white rounded-2xl p-5" data-testid="scan-call-2">
                                    <div className="flex items-center gap-3">
                                        <Phone size={32} weight="fill" />
                                        <div>
                                            <div className="font-display text-xl">{p.emergency_contact_2_name || t.scan.callNow}</div>
                                            <div className="font-mono text-sm opacity-90">{p.emergency_contact_2_phone}</div>
                                        </div>
                                    </div>
                                </a>
                            )}
                        </div>
                    )}

                    {/* Medical info */}
                    <div className="mt-6 space-y-4">
                        {p.allergies && <Block icon={<Warning weight="duotone" />} label={t.scan.allergies} value={p.allergies} critical testid="scan-allergies" />}
                        {p.medical_conditions && <Block icon={<FirstAid weight="duotone" />} label={t.scan.conditions} value={p.medical_conditions} testid="scan-conditions" />}
                        {p.medications && <Block icon={<Pill weight="duotone" />} label={t.scan.medications} value={p.medications} testid="scan-medications" />}
                        {p.notes && <Block icon={<UsersThree weight="duotone" />} label={t.scan.notes} value={p.notes} testid="scan-notes" />}
                        {p.address && <Block icon={<MapPin weight="duotone" />} label={t.scan.address} value={p.address} />}
                    </div>

                    <div className="text-center text-[10px] font-mono tracking-widest text-brand-textMuted py-8">
                        QR BRACELET PRO • {code}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Block({ icon, label, value, critical, testid }) {
    return (
        <div className={`rounded-2xl p-4 border ${critical ? "bg-brand-critical/5 border-brand-critical/30" : "bg-brand-bg border-brand-border"}`} data-testid={testid}>
            <div className={`flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase ${critical ? "text-brand-critical" : "text-brand-textMuted"}`}>
                <span className={critical ? "text-brand-critical" : "text-brand-text/60"}>{icon}</span>
                {label}
            </div>
            <div className={`mt-2 text-xl leading-snug whitespace-pre-wrap ${critical ? "font-display text-brand-critical" : "text-brand-text"}`}>{value}</div>
        </div>
    );
}

function StatusScreen({ icon, msg, code }) {
    return (
        <div className="min-h-screen bg-brand-bg flex items-center justify-center px-5" data-testid="scan-status">
            <div className="text-center max-w-sm">
                <div className="text-brand-textMuted">{icon}</div>
                <div className="font-display text-2xl mt-4">{msg}</div>
                <div className="font-mono text-xs tracking-widest text-brand-textMuted mt-2">CODE: {code}</div>
            </div>
        </div>
    );
}

function Mini({ text }) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-xs tracking-widest text-brand-textMuted">{text}</div>;
}

function normalizeUrl(u) {
    if (!u) return "#";
    if (/^https?:\/\//i.test(u)) return u;
    return `https://${u}`;
}
